import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../../services/accountingApi';
import { AccountingAccount } from '../../types/accounting';
import '../../pages/Accounting.css';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  account: AccountingAccount | null;
}

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose, onSaved, account }) => {
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<'bank' | 'cash'>('bank');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (account) {
      setName(account.name);
      setAccountType(account.account_type);
      setOpeningBalance(String(account.opening_balance));
      setNotes(account.notes || '');
    } else {
      setName('');
      setAccountType('bank');
      setOpeningBalance('0');
      setNotes('');
    }
    setErrors({});
  }, [isOpen, account]);

  if (!isOpen) return null;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Account name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: name.trim(),
      accountType,
      openingBalance: parseFloat(openingBalance || '0'),
      notes: notes.trim() || undefined
    };

    try {
      setLoading(true);
      if (account) {
        await accountingApi.updateAccount(account.id, payload);
        toast.success('Account updated successfully');
      } else {
        await accountingApi.createAccount(payload);
        toast.success('Account created successfully');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="acc-modal-overlay" onClick={onClose}>
      <div className="acc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acc-modal-header">
          <h2>{account ? 'Edit Account' : 'Add Account'}</h2>
          <button className="acc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="acc-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="acc-form-group">
              <label>Account Name <span className="acc-required">*</span></label>
              <input type="text" className={errors.name ? 'error' : ''} value={name} onChange={(e) => setName(e.target.value)} disabled={loading} placeholder="e.g. ADCB Bank, Petty Cash" maxLength={150} />
              {errors.name && <div className="acc-error-message">{errors.name}</div>}
            </div>

            <div className="acc-form-row">
              <div className="acc-form-group">
                <label>Type</label>
                <select value={accountType} onChange={(e) => setAccountType(e.target.value as 'bank' | 'cash')} disabled={loading}>
                  <option value="bank">Bank</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div className="acc-form-group">
                <label>Opening Balance (AED)</label>
                <input type="number" step="0.01" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={loading || !!account} />
              </div>
            </div>
            {account && (
              <p style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '-0.5rem' }}>
                Opening balance can't be edited after creation to keep historical balances consistent.
              </p>
            )}

            <div className="acc-form-group">
              <label>Notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={loading} maxLength={255} />
            </div>

            <div className="acc-form-actions">
              <button type="button" className="acc-btn acc-btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
              <button type="submit" className="acc-btn acc-btn-primary" disabled={loading}>{loading ? 'Saving...' : (account ? 'Save Changes' : 'Add Account')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountModal;
